/**
 * Required Minimum Distribution amounts (spec §9).
 *
 *  - Lifetime RMDs on pre-tax, RMD-eligible accounts (Uniform Lifetime Table,
 *    SECURE 2.0 starting ages).
 *  - Inherited IRAs (spec §4): SECURE Act 10-year rule for non-eligible
 *    designated beneficiaries, with annual RMDs during the window when the
 *    decedent had already begun RMDs (per the 2024 final regulations);
 *    eligible designated beneficiaries stretch over single life expectancy
 *    (reduce-by-one). Inherited Roth distributions are tax-free but the
 *    10-year emptying rule still applies.
 *
 * Joint Life Table (spouse >10y younger, sole beneficiary) still to come.
 */
import type { Account } from '../../model/types';
import { ACCOUNT_META } from '../../model/accountMeta';
import { rmdStartAge, uniformLifetimeDivisor } from '../data/rmd';
import { singleLifeExpectancy } from '../data/singleLife';

export interface RmdResult {
  accountId: string;
  age: number;
  divisor: number;
  amount: number;
}

/** Whether this account owner must take a LIFETIME RMD at `ageThisYear`.
 *  (Inherited accounts follow their own rules — see computeInheritedRmd.) */
export function rmdRequired(account: Account, birthYear: number, ageThisYear: number): boolean {
  if (account.inherited) return false;
  if (!ACCOUNT_META[account.type].rmdEligible) return false;
  return ageThisYear >= rmdStartAge(birthYear);
}

export interface InheritedRmdResult {
  accountId: string;
  amount: number;
  /** Ordinary-taxable portion (0 for inherited Roth). */
  taxableAmount: number;
  /** True when this is the year-10 forced full distribution. */
  fullDistribution: boolean;
}

/**
 * RMD for an inherited IRA this year.
 * @param beneficiaryBirthYear birth year of the household member who inherited
 */
export function computeInheritedRmd(
  account: Account,
  balance: number,
  beneficiaryBirthYear: number,
  year: number,
): InheritedRmdResult {
  const none: InheritedRmdResult = {
    accountId: account.id, amount: 0, taxableAmount: 0, fullDistribution: false,
  };
  const info = account.inherited;
  if (!info || balance <= 0) return none;
  if (year <= info.decedentDeathYear) return none; // RMDs start the year after death

  const isRoth = account.type === 'inherited_ira_roth';
  const taxable = (amt: number) => (isRoth ? 0 : amt);

  // Reduce-by-one single-life divisor, fixed at the beneficiary's age in the
  // year after death.
  const firstRmdYear = info.decedentDeathYear + 1;
  const ageAtFirst = firstRmdYear - beneficiaryBirthYear;
  const divisor = singleLifeExpectancy(ageAtFirst) - (year - firstRmdYear);

  if (info.beneficiaryClass === 'eligible_designated') {
    // Stretch over life expectancy; no 10-year cap.
    if (divisor <= 1) {
      return { accountId: account.id, amount: balance, taxableAmount: taxable(balance), fullDistribution: true };
    }
    const amount = balance / divisor;
    return { accountId: account.id, amount, taxableAmount: taxable(amount), fullDistribution: false };
  }

  // Non-eligible designated beneficiary: empty by end of year 10 after death.
  const finalYear = info.decedentDeathYear + 10;
  if (year >= finalYear) {
    return { accountId: account.id, amount: balance, taxableAmount: taxable(balance), fullDistribution: true };
  }
  // Annual RMDs during the window only when the decedent had begun RMDs
  // (and never for inherited Roth — the decedent had no RMD obligation).
  if (info.decedentHadBegunRMDs && !isRoth && divisor > 1) {
    const amount = balance / divisor;
    return { accountId: account.id, amount, taxableAmount: amount, fullDistribution: false };
  }
  return none;
}

/**
 * RMD for one account this year. Uses the balance passed in (which the engine
 * treats as the prior-year-end balance).
 */
export function computeRmd(
  account: Account,
  priorYearEndBalance: number,
  birthYear: number,
  ageThisYear: number,
): RmdResult {
  if (!rmdRequired(account, birthYear, ageThisYear) || priorYearEndBalance <= 0) {
    return { accountId: account.id, age: ageThisYear, divisor: 0, amount: 0 };
  }
  const divisor = uniformLifetimeDivisor(ageThisYear);
  return {
    accountId: account.id,
    age: ageThisYear,
    divisor,
    amount: priorYearEndBalance / divisor,
  };
}
